import {Component, effect, inject, OnInit, signal} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {Card} from 'primeng/card';
import {forkJoin} from 'rxjs';
import {
  ALL_SECTIONS,
  OVERVIEW_FIELD_MAP,
  Step12ProjectCharterOverviewComponent,
} from './step-1-2-project-charter-overview/step-1-2-project-charter-overview.component';
import {
  SCOPE_FIELD_MAP,
  SCOPE_SECTIONS,
  Step12ProjectChartScopeComponent,
} from './step-1-2-project-charter-scope/step-1-2-project-chart-scope.component';
import {
  PARAMETERS_FIELD_MAP,
  PARAMETERS_SECTIONS,
  Step12ProjectCharterParametersComponent,
} from './step-1-2-project-charter-parameters/step-1-2-project-charter-parameters.component';
import {
  ROLES_AND_STAKEHOLDERS_FIELD_MAP,
  ROLES_AND_STAKEHOLDERS_SECTIONS,
  Step12ProjectCharterRolesAndStakeholdersComponent,
} from './step-1-2-project-charter-roles-and-stakeholders/step-1-2-project-charter-roles-and-stakeholders.component';
import {MultiTabStepBaseComponent} from '../../../../../../shared/tabs/multi-tab-step-base.component';
import {TabStatus} from '../../../../../../shared/tabs/tab-status.model';
import {TabStatusDirective} from '../../../../../../shared/tabs/tab-status.directive';
import {Step12ProjectCharterService} from './step-1-2-project-charter.service';
import {
  ProjectCharterOverviewResponse,
  ProjectCharterParametersResponse,
  ProjectCharterRolesAndStakeholdersResponse,
  ProjectCharterScopeResponse,
  ProjectRoleType,
  ProjectStepType,
} from '@frontend/shared-api';
import {Step12ProjectCharterTimeFrameComponent} from './step-1-2-project-charter-time-frame/step-1-2-project-charter-time-frame.component';
import {ProjectTimeFrameService} from './step-1-2-project-charter-time-frame/step-1-2-project-time-frame.service';
import {StepDotMenuComponent} from '../../../../../../shared/components/step-dot-menu/step-dot-menu.component';
import {calculateRichTextSectionStatus} from '../../../../../../shared/services/project-step-rich-text.service';
import {createTabStatusSignal} from '../../../../../../shared/tabs/tab-utils';
import {TabsUnsavedRevealDirective} from '../../../../../../shared/tabs/tabs-unsaved-reveal.directive';

@Component({
  selector: 'app-step-1-2-project-charter-wrapper',
  imports: [
    TranslocoPipe,
    Tab,
    TabList,
    Tabs,
    TabPanels,
    TabPanel,
    Card,
    Step12ProjectCharterOverviewComponent,
    Step12ProjectChartScopeComponent,
    Step12ProjectCharterParametersComponent,
    Step12ProjectCharterRolesAndStakeholdersComponent,
    Step12ProjectCharterTimeFrameComponent,
    StepDotMenuComponent,
    TabStatusDirective,
    TabsUnsavedRevealDirective,
  ],
  templateUrl: './step-1-2-project-charter.component.html',
})
export class Step12ProjectCharterComponent extends MultiTabStepBaseComponent implements OnInit {
  private readonly charterService = inject(Step12ProjectCharterService);
  private readonly timeFrameService = inject(ProjectTimeFrameService);

  protected readonly overviewTabStatus = createTabStatusSignal();
  protected readonly scopeTabStatus = createTabStatusSignal();
  protected readonly parametersTabStatus = createTabStatusSignal();
  protected readonly rolesAndStakeholdersTabStatus = createTabStatusSignal();
  readonly timeFrameTabStatus = createTabStatusSignal();

  protected readonly contributorRole = ProjectRoleType.CONTRIBUTOR;
  protected readonly projectChartaStep = ProjectStepType.PROJECT_CHARTER;

  overviewData = signal<ProjectCharterOverviewResponse | undefined>(undefined);
  scopeData = signal<ProjectCharterScopeResponse | undefined>(undefined);
  parametersData = signal<ProjectCharterParametersResponse | undefined>(undefined);
  rolesAndStakeholdersData = signal<ProjectCharterRolesAndStakeholdersResponse | undefined>(
    undefined,
  );

  constructor() {
    super();

    effect(() => {
      this.calculateTimeFrameStatus();
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadAllCharterData();
    this.charterService.loadCharter();
    this.timeFrameService.loadTimeFrames();
  }

  // refactoring: move loading and data management into service
  private loadAllCharterData(): void {
    forkJoin({
      overview: this.charterService.getCharterOverview(this.projectId),
      scope: this.charterService.getCharterScope(this.projectId),
      parameters: this.charterService.getCharterParameters(this.projectId),
      rolesAndStakeholders: this.charterService.getCharterRolesAndStakeholders(this.projectId),
    }).subscribe({
      next: (data) => {
        this.overviewData.set(data.overview);
        this.scopeData.set(data.scope);
        this.parametersData.set(data.parameters);
        this.rolesAndStakeholdersData.set(data.rolesAndStakeholders);

        this.calculateInitialTabStatuses();
      },
    });
  }

  private calculateInitialTabStatuses(): void {
    const overviewData = this.overviewData();
    if (overviewData) {
      const richTextCompleted = calculateRichTextSectionStatus(
        overviewData,
        ALL_SECTIONS,
        OVERVIEW_FIELD_MAP,
      );

      const dropdownCompleted = this.calculateDropdownStatus(overviewData);
      const overviewStatus = {
        totalSections: ALL_SECTIONS.length + 3,
        completedSections: richTextCompleted + dropdownCompleted,
      };
      this.onOverviewStatusChange(overviewStatus);
    }

    const scopeData = this.scopeData();
    if (scopeData) {
      const completedSections = calculateRichTextSectionStatus(
        scopeData,
        SCOPE_SECTIONS,
        SCOPE_FIELD_MAP,
      );
      const scopeStatus = {
        totalSections: SCOPE_SECTIONS.length,
        completedSections,
      };
      this.onScopeStatusChange(scopeStatus);
    }

    const parametersData = this.parametersData();
    if (parametersData) {
      const completedSections = calculateRichTextSectionStatus(
        parametersData,
        PARAMETERS_SECTIONS,
        PARAMETERS_FIELD_MAP,
      );
      const parametersStatus = {
        totalSections: PARAMETERS_SECTIONS.length,
        completedSections,
      };
      this.onParametersStatusChange(parametersStatus);
    }

    const rolesAndStakeholdersData = this.rolesAndStakeholdersData();
    if (rolesAndStakeholdersData) {
      const completedSections = calculateRichTextSectionStatus(
        rolesAndStakeholdersData,
        ROLES_AND_STAKEHOLDERS_SECTIONS,
        ROLES_AND_STAKEHOLDERS_FIELD_MAP,
      );
      const rolesAndStakeholdersStatus = {
        totalSections: ROLES_AND_STAKEHOLDERS_SECTIONS.length,
        completedSections,
      };
      this.onRolesAndStakeholdersStatusChange(rolesAndStakeholdersStatus);
    }
  }

  private calculateTimeFrameStatus(): void {
    const totalSections = this.timeFrameService.getNumTotalSections();
    const completedSections = this.timeFrameService.getNumCompletedSections();
    if (totalSections > 0 && completedSections) {
      const timeFrameStatus: TabStatus = {
        totalSections: totalSections,
        completedSections: completedSections,
      };
      this.onTimeFrameStatusChange(timeFrameStatus);
    }
  }

  private calculateDropdownStatus(overviewData: ProjectCharterOverviewResponse): number {
    let count = 0;
    if (overviewData.companyValue1?.trim()) count++;
    if (overviewData.companyValue2?.trim()) count++;
    if (overviewData.companyValue3?.trim()) count++;
    return count;
  }

  onOverviewStatusChange(status: TabStatus): void {
    this.overviewTabStatus.update(status);
  }

  onScopeStatusChange(status: TabStatus): void {
    this.scopeTabStatus.update(status);
  }

  onParametersStatusChange(status: TabStatus): void {
    this.parametersTabStatus.update(status);
  }

  onRolesAndStakeholdersStatusChange(status: TabStatus): void {
    this.rolesAndStakeholdersTabStatus.update(status);
  }

  onTimeFrameStatusChange(status: TabStatus): void {
    this.timeFrameTabStatus.update(status);
  }
}
